import { useNavigate } from 'react-router-dom'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { ReviewsDataTableDocument } from 'generated/resources/datatable.graphql'
import { DuplicateReviewDocument, PerformanceReviewsReview } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { usePerformanceModuleContext } from 'modules/performance/hooks/usePerfomanceModuleContext'

import DeleteReviewModal from './DeleteReviewModal'
import { canEditReview } from './helpers/canEditReview'

interface Props {
  review: PerformanceReviewsReview
}

function Settings({ review }: Props) {
  const { navigation } = useNavigation()

  const [showDestroyModal, toggleDestroyModal] = useToggle()
  const { hasFullVisibility } = usePerformanceModuleContext()
  const canEdit = canEditReview(review.role, hasFullVisibility)
  const [open, toggle] = useToggle()
  const navigate = useNavigate()

  const trackAndNavigate = () => {
    if (canEdit) {
      track(trackingEvents.reviews.reviewConfiguration)
      navigate(navigation.performance.reviews.id.configuration.fullPath({ id: Number(review.id) }))
    } else {
      navigate(navigation.performance.reviews.id.reviews.fullPath({ id: Number(review.id) }))
    }
  }

  const [duplicateReview] = useMutation(DuplicateReviewDocument, {
    extractorFn: (data) => data.performanceReviewsMutations.duplicatePerformancereviewsReview,
  })

  const duplicateReviewAction = async () => {
    app.setProgress({
      message: i18n.t('surveys.index.table.row.edit.duplicating'),
    })

    const response = await duplicateReview({
      awaitRefetchQueries: true,
      variables: {
        id: review.id,
      },
      refetchQueries: [ReviewsDataTableDocument],
    })
    app.setProgress(null)

    const handled = handleResult(response, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_error') })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
  }

  const popoverOptions = () => {
    const options: Option[] = [
      {
        title: i18n.t('surveys.index.table.row.edit.delete'),
        type: 'wrong',
        onClick: toggleDestroyModal(true),
      },
    ]

    if (canEdit) {
      options.unshift({
        title: i18n.t('surveys.index.table.row.edit.duplicate'),
        type: 'primary',
        onClick: duplicateReviewAction,
      })
    }

    return options
  }

  return (
    <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
      {canEdit && (
        <Popover
          attachment="top right"
          targetAttachment="bottom center"
          toggle={toggle}
          open={open}
        >
          <Button
            onClick={toggle(true)}
            type="secondary"
            iconName="ellipsis"
            aria-label="Access review actions"
          />
          <PopoverContent onClose={toggle(false)} options={popoverOptions()} />
        </Popover>
      )}
      <Button
        onClick={() => trackAndNavigate()}
        type="secondary"
        iconName="arrow-right"
        aria-label="Access review"
      />
      {showDestroyModal && (
        <DeleteReviewModal review={review} onClose={toggleDestroyModal(false)} />
      )}
    </Box>
  )
}

export default observer(Settings)
